// Agenda con reservar/reagendar/cancelar (Rediseño mayor 2026-09-02, Fase
// 4 "destinos reales del nav") — envuelve el MISMO CalendarioCitas.tsx del
// Dashboard (día/semana/mes/lista ya existentes), pasándole los handlers
// nuevos de reagendar/cancelar (el Dashboard no los pasa, sigue de solo
// lectura). "Nueva cita" abre NuevaCitaDialog.tsx.
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/sonner';
import { crearUltimaPeticion } from '@/lib/ultimaPeticion';
import { useEventosSala } from '@/lib/useEventosSala';
import { CalendarioCitas } from '@/dashboard/CalendarioCitas';
import { IndicadorEnVivo } from '@/dashboard/IndicadorEnVivo';
import { TopNav } from '@/dashboard/TopNav';
import {
  cancelarCitaHce,
  obtenerCitasDashboard,
  reagendarCitaHce,
  type CitaDashboard,
  type CitasDashboard,
  type RangoDashboard,
} from '@/lib/hceVault';
import { NuevaCitaDialog } from './NuevaCitaDialog';

type Vista = RangoDashboard | 'lista';

export function AgendaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vista, setVista] = useState<Vista>('dia');
  const [fecha, setFecha] = useState(() => new Date());
  const [citas, setCitas] = useState<CitasDashboard | null>(null);
  const [cargando, setCargando] = useState(true);
  const [nuevaCitaAbierta, setNuevaCitaAbierta] = useState(false);
  const [citaAReagendar, setCitaAReagendar] = useState<CitaDashboard | null>(null);
  const [nuevaFechaHora, setNuevaFechaHora] = useState('');

  const rangoParaBackend: RangoDashboard = vista === 'lista' ? 'semana' : vista;
  // Solo la recarga más reciente escribe el estado: una respuesta vieja (otro rango/fecha, o dos silenciosas en vuelo) no pisa a la nueva.
  const ultima = useRef(crearUltimaPeticion());

  // `silencioso`: recarga disparada por el canal en vivo o por el respaldo periódico; no parpadea ni avisa de errores pasajeros.
  const cargar = useCallback(async (silencioso = false) => {
    const peticion = ultima.current.nueva();
    if (!silencioso) setCargando(true);
    try {
      const datos = await obtenerCitasDashboard(rangoParaBackend, fecha);
      if (peticion.vigente()) setCitas(datos);
    } catch {
      if (!silencioso && peticion.vigente()) notify({ color: 'red', message: t('dashboard.errorCargar') });
    }
    if (peticion.vigente()) setCargando(false);
  }, [rangoParaBackend, fecha, t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Canal en vivo (Sala CRM<->HCE, rebanada 3): la agenda se refresca cuando cambia el calendario.
  const { estado: enVivo } = useEventosSala(() => void cargar(true), { tipos: ['calendario'] });

  async function confirmarReagendar(): Promise<void> {
    if (!citaAReagendar || !nuevaFechaHora) return;
    const r = await reagendarCitaHce(citaAReagendar.citaId, new Date(nuevaFechaHora).toISOString());
    if (!r.ok) {
      notify({ color: 'red', message: r.detail, autoClose: false });
      return;
    }
    notify({ color: 'green', message: t('agenda.reagendada') });
    setCitaAReagendar(null);
    setNuevaFechaHora('');
    void cargar();
  }

  async function cancelar(cita: CitaDashboard): Promise<void> {
    if (!window.confirm(t('agenda.confirmarCancelar'))) return;
    const r = await cancelarCitaHce(cita.citaId, t('agenda.motivoCancelacionDefault'));
    if (!r.ok) {
      notify({ color: 'red', message: r.detail, autoClose: false });
      return;
    }
    notify({ color: 'green', message: t('agenda.cancelada') });
    void cargar();
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4 py-4">
      <TopNav activo="agenda" />
      <IndicadorEnVivo estado={enVivo} />
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-medium text-hce-ink">{t('agenda.titulo')}</h1>
        <Button size="sm" onClick={() => setNuevaCitaAbierta(true)}>
          {t('agenda.nuevaCita')}
        </Button>
      </div>
      {citas?.sinEspecialidadVinculada && <p className="text-sm text-hce-muted">{t('dashboard.sinVinculo')}</p>}

      <CalendarioCitas
        vista={vista}
        onVistaChange={setVista}
        fecha={fecha}
        onFechaChange={setFecha}
        citas={citas?.citas ?? []}
        cargando={cargando}
        onIngresarPaciente={(c) => c.medplumPatientId && void navigate(`/${c.medplumPatientId}`)}
        onUnirseVideollamada={(c) => c.medplumPatientId && void navigate(`/telemedicina/${c.citaId}/${c.medplumPatientId}`)}
        onReagendar={(c) => setCitaAReagendar(c)}
        onCancelar={(c) => void cancelar(c)}
      />

      <NuevaCitaDialog
        abierto={nuevaCitaAbierta}
        onCerrar={() => setNuevaCitaAbierta(false)}
        onCreada={() => {
          setNuevaCitaAbierta(false);
          notify({ color: 'green', message: t('agenda.reservada') });
          void cargar();
        }}
      />

      <Dialog open={citaAReagendar !== null} onOpenChange={(v) => !v && setCitaAReagendar(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('agenda.reagendar')}</DialogTitle>
          </DialogHeader>
          <Input type="datetime-local" value={nuevaFechaHora} onChange={(e) => setNuevaFechaHora(e.target.value)} />
          <DialogFooter>
            <Button disabled={!nuevaFechaHora} onClick={() => void confirmarReagendar()}>
              {t('agenda.confirmar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
